import React, { useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FaArrowLeft, FaCalendarAlt, FaClock, FaMapMarkerAlt, FaImage, FaLocationArrow } from 'react-icons/fa';
import DatePicker from 'react-datepicker';
import { supabase } from '../data/supabaseClient';
import { AuthContext } from '../Provider';
import { CustomButton2, CustomButton8, CustomInputBox1 } from '../Templates';
import EventLocationMap from './components/EventLocationMap';
import { useToast } from '../hooks/ToastContext';
import Spinner from '../common/Spinner';
import 'react-datepicker/dist/react-datepicker.css';
import './styles/CreateEventPage.css';

function CreateEventPage() {
  const navigate = useNavigate();
  const { t } = useTranslation('common');
  const { user } = useContext(AuthContext);
  const { addToast } = useToast();
  
  // Form state
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    location: '',
    date: new Date(new Date().setHours(new Date().getHours() + 2)), // 2 hours from now
    time: new Date(new Date().setHours(new Date().getHours() + 2)),
    category: 'other',
    bannerImage: null,
    bannerPreview: null
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  
  // Event categories
  const categories = [
    { value: 'music', label: t('events.categories.music') },
    { value: 'sports', label: t('events.categories.sports') },
    { value: 'culture', label: t('events.categories.culture') },
    { value: 'education', label: t('events.categories.education') },
    { value: 'food', label: t('events.categories.food') },
    { value: 'nightlife', label: t('events.categories.nightlife') },
    { value: 'community', label: t('events.categories.community') },
    { value: 'other', label: t('events.categories.other') },
  ];

  // Handle input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: null
      }));
    }
  };

  // Handle date change
  const handleDateChange = (date) => {
    setFormData(prev => ({
      ...prev,
      date
    }));
    
    if (errors.date) {
      setErrors(prev => ({
        ...prev,
        date: null
      }));
    }
  };

  // Handle time change
  const handleTimeChange = (time) => {
    setFormData(prev => ({
      ...prev,
      time
    }));
    
    if (errors.time) {
      setErrors(prev => ({
        ...prev,
        time: null
      }));
    }
  };

  // Handle image upload
  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Validate file size (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      setErrors(prev => ({
      ...prev,
      bannerImage: t('errors.file_too_large')
      }));
    addToast({
      type: 'error',
      title: t('errors.validation_error'),
      description: t('errors.file_too_large')
    });
      return;
    }
    
    // Validate file type
    const validTypes = ['image/jpeg', 'image/png', 'image/jpg', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      setErrors(prev => ({
      ...prev,
      bannerImage: t('errors.invalid_file_type')
      }));
    addToast({
      type: 'error',
      title: t('errors.validation_error'),
      description: t('errors.invalid_file_type')
    });
      return;
    }
    
    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setFormData(prev => ({
        ...prev,
        bannerImage: file,
        bannerPreview: reader.result
      }));
    };
    reader.readAsDataURL(file);
    
    // Clear error
    setErrors(prev => ({
      ...prev,
      bannerImage: null
    }));
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};
    let hasErrors = false;
    
    if (!formData.title.trim()) {
      newErrors.title = t('errors.title_required');
      hasErrors = true;
    }
    
    if (!formData.description.trim()) {
      newErrors.description = t('errors.description_required');
      hasErrors = true;
    }
    
    if (!formData.location.trim()) {
      newErrors.location = t('errors.location_required');
      hasErrors = true;
    }
    
    if (!formData.date) {
      newErrors.date = t('errors.date_required');
      hasErrors = true;
    }
    
    if (!formData.time) {
      newErrors.time = t('errors.time_required');
      hasErrors = true;
    }
    
    // Check if event is in the past
    const eventDateTime = new Date(
      formData.date.getFullYear(),
      formData.date.getMonth(),
      formData.date.getDate(),
      formData.time.getHours(),
      formData.time.getMinutes()
    );
    
    if (eventDateTime < new Date()) {
      newErrors.date = t('errors.event_in_past');
      hasErrors = true;
    }
    
    // Max attendees validation removed
    
    setErrors(newErrors);
    
    // Show toast with validation errors
    if (hasErrors) {
      const firstError = Object.values(newErrors)[0];
      addToast({
        type: 'error',
        title: t('errors.validation_error'),
        description: firstError
      });
    }
    
    return !hasErrors;
  };

  // Create event
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate form
    if (!validateForm()) {
      // Scroll to first error
      const firstErrorField = Object.keys(errors)[0];
      if (firstErrorField) {
        document.getElementsByName(firstErrorField)[0]?.scrollIntoView({
          behavior: 'smooth',
          block: 'center'
        });
      }
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      // Combine date and time
      const eventDateTime = new Date(
        formData.date.getFullYear(),
        formData.date.getMonth(),
        formData.date.getDate(),
        formData.time.getHours(),
        formData.time.getMinutes()
      );
      
      // Calculate end time (default to 3 hours later)
      const endDateTime = new Date(eventDateTime);
      endDateTime.setHours(endDateTime.getHours() + 3);
      
      // Upload image if provided
      let bannerImageUrl = null;
      if (formData.bannerImage) {
        try {
          // Use public bucket instead of events bucket
          const bucketName = 'events';
          
          const fileExt = formData.bannerImage.name.split('.').pop();
          const fileName = `${user.id}_${Date.now()}.${fileExt}`;
          const filePath = `event_banners/${fileName}`;
          
          // Upload the file
          const { data: uploadData, error: uploadError } = await supabase.storage
            .from(bucketName)
            .upload(filePath, formData.bannerImage);
            
          if (uploadError) {
            console.error('Upload error:', uploadError);
            // Don't throw - continue without image
            addToast({
              type: 'warning',
              title: t('errors.image_upload_failed'),
              description: t('errors.continuing_without_image')
            });
          } else if (uploadData) {
            // Get public URL
            const { data: publicUrlData } = supabase.storage
              .from(bucketName)
              .getPublicUrl(filePath);
              
            bannerImageUrl = publicUrlData.publicUrl;
          }
        } catch (uploadErr) {
          console.error('Image upload failed:', uploadErr);
          // Continue without image if upload fails
          addToast({
            type: 'warning',
            title: t('errors.image_upload_failed'),
            description: t('errors.continuing_without_image')
          });
        }
      }
      
      // Create event in database
      const { data, error } = await supabase
        .from('events')
        .insert({
          title: formData.title,
          description: formData.description,
          location: formData.location,
          start_date: eventDateTime.toISOString(),
          end_date: endDateTime.toISOString(),
          creator_id: user.id,
          banner_image_url: bannerImageUrl,
          category: formData.category,
          max_attendees: null, // Removed maxAttendees field
          is_public: true
        })
        .select();
        
      if (error) {
        throw error;
      }
      
      // Automatically add creator as attending
      const eventId = data[0].id;
      await supabase
        .from('event_attendees')
        .insert({
          event_id: eventId,
          user_id: user.id,
          status: 'going'
        });
      
      // Show success message
      addToast({
        type: 'success',
        title: t('events.event_created'),
        description: t('events.event_created_success')
      });
      
      // Navigate to event page
      navigate(`/events/${eventId}`);
    } catch (err) {
      console.error('Error creating event:', err);
      
      addToast({
        type: 'error',
        title: t('errors.creation_failed'),
        description: t('errors.event_creation_failed')
      });
      
      setIsSubmitting(false);
    }
  };

  const handleBackClick = () => {
    navigate('/events');
  };

  return (
    <>
      <div className="create-event-page">
        <div className="create-event-header">
          <div className="back-button-container">
            <CustomButton8 onClick={handleBackClick}>
              <FaArrowLeft />
            </CustomButton8>
          </div>
          <h1>{t('events.create_new_event')}</h1>
        </div>
        
        <form className="event-form" onSubmit={handleSubmit}>
          <div className={`form-group ${errors.title ? 'has-error' : ''}`}>
            <label>{t('events.event_title')}</label>
            <CustomInputBox1
              type="text"
              name="title"
              placeholder={t('events.event_title_placeholder')}
              value={formData.title}
              onChange={handleInputChange}
              maxLength={100}
              error={errors.title}
            />
            {errors.title && <div className="error-message">{errors.title}</div>}
          </div>
          
          <div className={`form-group ${errors.description ? 'has-error' : ''}`}>
            <label>{t('events.event_description')}</label>
            <textarea
              name="description"
              placeholder={t('events.event_description_placeholder')}
              value={formData.description}
              onChange={handleInputChange}
              rows={4}
              className={`custom-textarea ${errors.description ? 'error' : ''}`}
            ></textarea>
            {errors.description && <div className="error-message">{errors.description}</div>}
          </div>
          
          <div className="form-row">
            <div className={`form-group ${errors.date ? 'has-error' : ''}`}>
              <label>{t('events.event_date')}</label>
              <div className="date-picker-container">
                <FaCalendarAlt className="icon" />
                <DatePicker
                  selected={formData.date}
                  onChange={handleDateChange}
                  dateFormat="dd/MM/yyyy"
                  minDate={new Date()}
                  className={`custom-date-picker ${errors.date ? 'error' : ''}`}
                />
              </div>
              {errors.date && <div className="error-message">{errors.date}</div>}
            </div>
            
            <div className={`form-group ${errors.time ? 'has-error' : ''}`}>
              <label>{t('events.event_time')}</label>
              <div className="date-picker-container">
                <FaClock className="icon" />
                <DatePicker
                  selected={formData.time}
                  onChange={handleTimeChange}
                  showTimeSelect
                  showTimeSelectOnly
                  timeIntervals={15}
                  timeCaption={t('events.time')}
                  dateFormat="h:mm aa"
                  className={`custom-date-picker ${errors.time ? 'error' : ''}`}
                />
              </div>
              {errors.time && <div className="error-message">{errors.time}</div>}
            </div>
          </div>
          
          <div className={`form-group ${errors.location ? 'has-error' : ''}`}>
            <label>
              <FaLocationArrow style={{ marginRight: '8px' }} />
              {t('events.event_location')}
            </label>
            <div className="google-places-container">
              <div className="event-location-map">
                <EventLocationMap 
                  onLocationSelect={(field, value) => {
                    if (field === 'address_to' && value?.formatted_address) {
                      setFormData(prev => ({
                        ...prev,
                        location: value.formatted_address
                      }));
                    }
                  }}
                />
              </div>
            </div>
            {errors.location && <div className="error-message">{errors.location}</div>}
          </div>
          
          <div className="form-group">
            <label>{t('events.event_category')}</label>
            <select
              name="category"
              value={formData.category}
              onChange={handleInputChange}
              className="custom-select"
            >
              {categories.map(category => (
                <option key={category.value} value={category.value}>
                  {category.label}
                </option>
              ))}
            </select>
          </div>
          
          <div className="form-group">
            <label>{t('events.event_banner')} ({t('events.optional')})</label>
            <div className="banner-upload-container">
              <div className="banner-preview">
                {formData.bannerPreview ? (
                  <img 
                    src={formData.bannerPreview} 
                    alt={t('events.event_banner_preview')} 
                  />
                ) : (
                  <div className="banner-placeholder">
                    <FaImage />
                    <span>{t('events.upload_banner')}</span>
                  </div>
                )}
              </div>
              
              <div className="banner-upload-button">
                <label className="custom-file-upload">
                  <input
                    type="file"
                    name="bannerImage"
                    accept="image/jpeg,image/png,image/jpg,image/webp"
                    onChange={handleImageChange}
                  />
                  {formData.bannerPreview ? 
                    t('events.change_image') : 
                    t('events.select_image')}
                </label>
                <p className="upload-hint">
                  {t('events.image_requirements')}
                </p>
              </div>
            </div>
            {errors.bannerImage && <div className="error-message">{errors.bannerImage}</div>}
          </div>
          
          <div className="form-actions">
            <CustomButton2 
              type="button"
              label={t('buttons.cancel')}
              onClick={handleBackClick}
              className="cancel-button"
            />
            <CustomButton2 
              type="submit"
              label={
                isSubmitting ? (
                  <div className="button-with-spinner">
                    <Spinner 
                      size="small" 
                      showText={false} 
                      thickness={2}
                      color="var(--BackG-color)"
                    />
                    <span>{t('buttons.creating')}</span>
                  </div>
                ) : t('buttons.create_event')
              }
              disabled={isSubmitting}
              className="create-button"
            />
          </div>
        </form>
      </div>
    </>
  );
}

export default CreateEventPage;